import React, { useEffect, useRef } from "react";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import StatusBadge from "./StatusBadge";
import { formatDateTime } from "./partnersMobileUtils";
import { getLedgerSyncStatus } from "../ledgerSyncUtils";
import css from "./partnersMobile.module.css";

// Labels and value styling deliberately mirror ViewCPDetails, so a user who
// knows the desktop modal reads the same page here.
function Row({ label, value, accent, action }) {
  return (
    <div className={css.kv}>
      <span className={css.kvKey}>{label}</span>
      <span
        className={css.kvValue}
        style={{ whiteSpace: "normal", color: accent ? "#1570ef" : undefined }}
      >
        {value || "NA"}
      </span>
      {action}
    </div>
  );
}

export default function PartnerDetail({
  row,
  docs,
  onBack,
  onApprove,
  onReject,
  onOverflow,
  canApprove,
  canReject,
  // Intentionally no record-to-record pager: opening a partner shows that
  // record only, and the user returns to the list to pick another.
  rejectLabel = "Reject",
}) {
  const safeDocs = docs ?? {};
  const showActions = canApprove || canReject;
  const sync = getLedgerSyncStatus(row);
  const bodyRef = useRef(null);

  // The body is its own scroll container, so window.scrollTo would do nothing.
  // Reset it whenever the record changes, per the prev/next requirement.
  useEffect(() => {
    if (bodyRef.current) bodyRef.current.scrollTop = 0;
  }, [row?.cpId]);

  const openUrl = (path) =>
    window.open(process.env.REACT_APP_SUPABASE_BUCKET_PREFIX + path, "_blank");

  // Same fallback chain ViewCPDetails uses for the GST holder name.
  const gstName =
    safeDocs.gst?.docHolderName && safeDocs.gst.docHolderName !== "NA"
      ? safeDocs.gst.docHolderName
      : row.gstBusinessName && row.gstBusinessName !== "NA"
      ? row.gstBusinessName
      : row.gstLegalName ?? "NA";

  return (
    <div className={`partnersMobileRoot ${css.detailRoot}`}>
      <div className={css.detailHeader}>
        <button type="button" className={css.kebab} aria-label="Back" onClick={onBack}>
          <ArrowBackIcon sx={{ fontSize: 20 }} />
        </button>
        <span className={css.detailHeaderTitle}>
          {row.billingName || row.name || "—"}
        </span>
        <button
          type="button"
          className={css.kebab}
          aria-label="More actions"
          onClick={onOverflow}
        >
          <MoreVertIcon sx={{ fontSize: 20 }} />
        </button>
      </div>

      <div className={css.detailBody} ref={bodyRef}>
        <div className={css.detailCard}>
          <div className={css.detailIdentity}>
            <span className={css.cardId}>{row.cpDisplayId}</span>
            <StatusBadge status={row.approvalStatus} disabled={row.Disabled} />
          </div>
        </div>

        {/* ViewCPDetails' top block: name, email | phone, CP Sourcing. */}
        <div className={css.detailCard}>
          <div className={css.detailCardTitle}>Contact</div>
          <Row label="Name" value={row.name} />
          <Row label="Email" value={row.email} />
          <Row label="Phone" value={row.contactNumber} />
          <Row
            label="CP Sourcing"
            value={
              row.cpSourcingName && row.cpSourcingName !== "NA"
                ? row.cpSourcingName
                : "-"
            }
          />
        </div>

        {/* ViewCPDetails' "General Details" block, same labels and order. */}
        <div className={css.detailCard}>
          <div className={css.detailCardTitle}>General Details</div>
          <Row label="Billing Name" value={row.billingName} />
          <Row label="Type" value={row.type} />
          <Row label="Recommended by" value={row.recommendedBySalesId || "-"} />
          <Row label="PAN Number" value={safeDocs.pan?.docNumber} />
          <Row label="PAN Name" value={safeDocs.pan?.docHolderName} accent />
          <Row
            label="RERA Number"
            value={safeDocs.rera?.docNumber}
            action={
              safeDocs.rera?.docUrl ? (
                <button
                  type="button"
                  aria-label="Open RERA document"
                  onClick={() => openUrl(safeDocs.rera.docUrl)}
                  style={{
                    border: "none",
                    background: "transparent",
                    cursor: "pointer",
                    color: "#344054",
                    minWidth: 44,
                    minHeight: 44,
                  }}
                >
                  <OpenInNewIcon sx={{ fontSize: 20 }} />
                </button>
              ) : null
            }
          />
          <Row label="RERA Name" value={safeDocs.rera?.docHolderName} accent />
          <Row label="GST Number" value={safeDocs.gst?.docNumber} />
          <Row label="GST Name" value={gstName} accent />
        </div>

        <div className={css.detailCard}>
          <div className={css.detailCardTitle}>Ledger Sync</div>
          <Row
            label="Sync Status"
            value={
              <span style={{ color: sync.color, fontWeight: 500 }}>{sync.label}</span>
            }
          />
          <Row label="Finance Ledger" value={sync.ledgerId ?? "—"} />
          <Row label="CRM Ledger" value={sync.crmLedgerId ?? "—"} />
        </div>

        <div className={css.detailCard}>
          <div className={css.detailCardTitle}>Bank Details</div>
          <Row label="Account Holder Name" value={row.bankAccountHolderName} />
          <Row
            label="Account Number"
            value={row.BankAccountNumberText ?? row.bankAccountNumber}
          />
          <Row label="IFSC Code" value={row.bankIFSCCode} />
          <Row label="Branch Name" value={row.bankBranchName} />
          <Row label="SignIn Date" value={formatDateTime(row.createdAt)} />
          <Row
            label="Last Updated Date"
            value={formatDateTime(row.lastUpdated)}
          />
        </div>
      </div>

      {showActions && (
        <div className={css.detailActions}>
          {canReject && (
            <button
              type="button"
              className={`${css.btn} ${css.btnDanger}`}
              onClick={onReject}
            >
              {rejectLabel}
            </button>
          )}
          {canApprove && (
            <button
              type="button"
              className={`${css.btn} ${css.btnPrimary}`}
              onClick={onApprove}
            >
              Approve
            </button>
          )}
        </div>
      )}
    </div>
  );
}
